import { useRef } from "react";
import { UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { MIGRATION } from "../../../constants/testIds";
import { parseFile } from "../../../lib/migrationParse";
import { Button } from "../../ui/button";

// SCR-P04-10 - multi-file CSV/Excel upload, parsed entirely client-side. Nothing here is ever
// sent to the server as raw content - only file name + row count travel with the draft.
export function UploadStep({ files, setFiles, onContinue }) {
  const inputRef = useRef(null);

  const handleFiles = async (fileList) => {
    const parsed = await Promise.all(Array.from(fileList).map(parseFile));
    const good = parsed.filter((p) => !p.error);
    const bad = parsed.filter((p) => p.error);
    bad.forEach((p) => toast.error(`${p.name}: ${p.error}`));
    setFiles((prev) => [...prev.filter((f) => !good.some((g) => g.name === f.name)), ...good]);
  };

  return (
    <div className="space-y-4">
      <div
        data-testid={MIGRATION.uploadDropzone}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="solid-surface rounded-[14px] p-10 text-center cursor-pointer border-2 border-dashed"
        style={{ borderColor: "var(--scarlet-hairline)" }}
      >
        <UploadCloud size={28} className="mx-auto mb-2" style={{ color: "var(--scarlet-text-secondary)" }} />
        <p className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>Drop CSV / Excel files here, or click to browse</p>
        <p className="text-xs mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Files are parsed in your browser - nothing is uploaded until you preview.</p>
        <input
          ref={inputRef} data-testid={MIGRATION.uploadFileInput} type="file" multiple accept=".csv,.xlsx,.xls" className="hidden"
          onChange={(e) => e.target.files.length && handleFiles(e.target.files)}
        />
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f) => (
            <div key={f.name} data-testid={MIGRATION.uploadFileRow(f.name)} className="solid-surface rounded-[10px] px-4 py-2.5 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>{f.name}</p>
                <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{f.rows.length} rows &middot; {f.headers.length} columns</p>
              </div>
              <button data-testid={MIGRATION.uploadFileRemove(f.name)} onClick={() => setFiles((prev) => prev.filter((x) => x.name !== f.name))} className="p-1.5 rounded-full hover:bg-black/5">
                <X size={14} style={{ color: "var(--scarlet-text-secondary)" }} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end">
        <Button data-testid={MIGRATION.wizardContinueButton} disabled={files.length === 0} onClick={onContinue} style={{ background: "var(--scarlet-accent)", color: "#fff" }}>
          Continue
        </Button>
      </div>
    </div>
  );
}
